import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { apiFetch, mediaUrl } from "./utils/api";
import { formatDate } from "./utils/time";
import Header from "./components/Header";
import BackButton from "./components/BackButton";
import Footer from "./components/Footer";
import { SkeletonArticle } from "./components/ui";
import NotAvailable from "./components/NotAvailable";
import "./Content.css";

/** Legacy posts predate widthPct and still carry the old size string. */
const LEGACY_WIDTH = { small: 25, medium: 50, large: 75, full: 100 };

/**
 * ONE sizing field: widthPct, a percentage of the column (10–100).
 *
 * It replaces the old `width` string + `maxHeight` pair, which fought each other —
 * max-height moved only the height while width stayed pinned at 100%, so media got
 * squashed instead of scaled. Now the percentage goes on the WRAPPER and the media
 * fills it at width:100%/height:auto, so both dimensions move together and nothing
 * can distort. maxHeight is no longer read for blog media at all.
 */
const widthPctOf = (block) => {
    const pct = Number(block?.widthPct);
    if (Number.isFinite(pct) && pct > 0) return Math.min(100, Math.max(10, pct));
    return LEGACY_WIDTH[block?.width] ?? 100;
};

/**
 * Media placement, shared by image and video figures. The width is handed to CSS as a
 * custom property rather than a plain inline `width` so the mobile breakpoint can still
 * override it — an inline width would win over the media query and leave a 25% image
 * stranded at 25% on a phone.
 *
 * align (left | full(center) | right) is unchanged; dAdmin names the centre value "full".
 */
const figureProps = (block) => ({
    className: `blog-block blog-figure align-${block.align || "full"}`,
    style: { "--fig-w": `${widthPctOf(block)}%` },
});

/**
 * One block of a post's blocks_json.
 *
 *   { type: 'heading' | 'text', html }                ← rich text from the dAdmin editor
 *   { type: 'image', src, alt, align, widthPct }
 *   { type: 'video', src, poster, align, widthPct }
 *   { type: 'divider' }                               ← a horizontal rule
 *   { type: 'row', cols: [...blocks] }                ← nests, so this recurses
 *
 * Unknown block types are skipped rather than throwing: the dAdmin editor will grow
 * new ones, and an unrecognised block should never take the whole post down.
 */
const Block = ({ block }) => {
    if (!block || typeof block !== "object") return null;

    switch (block.type) {
        case "heading":
        case "text":
            if (!block.html) return null;
            return (
                <div
                    className="blog-block prose"
                    dangerouslySetInnerHTML={{ __html: block.html }}
                />
            );

        case "image":
            if (!block.src) return null;
            return (
                <figure {...figureProps(block)}>
                    <img src={mediaUrl(block.src)} alt={block.alt || ""} loading="lazy" />
                </figure>
            );

        case "video":
            if (!block.src) return null;
            return (
                <figure {...figureProps(block)}>
                    <video
                        src={mediaUrl(block.src)}
                        poster={block.poster ? mediaUrl(block.poster) : undefined}
                        controls
                        playsInline
                        preload="metadata"
                    />
                </figure>
            );

        case "divider":
            return <hr className="blog-block blog-divider" />;

        case "row":
            if (!Array.isArray(block.cols) || !block.cols.length) return null;
            return (
                <div className="blog-block blog-row">
                    {block.cols.map((col) => (
                        <div className="blog-col" key={col.id}>
                            <Block block={col} />
                        </div>
                    ))}
                </div>
            );

        default:
            return null;
    }
};

/** Blog is fully public (no audience columns yet), but the server only ever serves
 *  status='published' — drafts and scheduled posts never leave the database. */
const BlogDetails = () => {
    const { id } = useParams();

    const [post, setPost] = useState(null);
    const [status, setStatus] = useState("loading"); // loading | ready | notfound

    useEffect(() => {
        let cancelled = false;

        (async () => {
            setStatus("loading");
            try {
                const res = await apiFetch(`/api/Dolluzcorp/blog/posts/${id}`);
                const data = await res.json();
                if (cancelled) return;

                if (res.ok && data.success) {
                    setPost(data.data);
                    setStatus("ready");
                } else {
                    setStatus("notfound");
                }
            } catch {
                if (!cancelled) setStatus("notfound");
            }
        })();

        return () => { cancelled = true; };
    }, [id]);

    return (
        <>
            <a className="skip-link" href="#main">Skip to content</a>
            <Header />

            <main id="main" className="article">
                <div className="page-wrap">
                    <BackButton fallback="/blog" />

                    {status === "loading" && <SkeletonArticle />}

                    {status === "notfound" && (
                        <NotAvailable kind="post" backTo="/blog" backLabel="Browse the blog" />
                    )}

                    {status === "ready" && post && (
                        <article className="blog-article">
                            <header className="article-head">
                                {post.category_name && <span className="chip chip-blog">{post.category_name}</span>}
                                <h1 className="article-title">{post.title}</h1>
                                {post.published_at && (
                                    <div className="article-meta">
                                        <span>Published</span>
                                        <time dateTime={post.published_at}>{formatDate(post.published_at)}</time>
                                    </div>
                                )}
                            </header>

                            <div className="blog-blocks article-body">
                                {post.blocks?.map((block) => (
                                    <Block key={block.id} block={block} />
                                ))}
                            </div>
                        </article>
                    )}
                </div>
            </main>

            <Footer />
        </>
    );
};

export default BlogDetails;
